// Pure helpers for the session-end recap screen. No DOM, no storage —
// callers pass the current cards + stats + snapshot history and get
// back the numbers to render.

import type { Card, CefrLevel, StatsMap } from "../types";
import { computeMasteryTier } from "./level";
import {
  dateNDaysAgo,
  findSnapshotOnOrBefore,
  oldestSnapshotDate,
  type ProgressSnapshot,
} from "../state/progress";

/** Box ≥ 5 in Leitner = "maîtrisé". Matches the `acquired` metric
 *  surfaced on the profile modal so the numbers line up. */
export function computeMasteredCount(
  cards: Card[],
  stats: StatsMap,
): number {
  let n = 0;
  for (const c of cards) {
    const s = stats[c.id];
    if (s && (s.box || 1) >= 5) n++;
  }
  return n;
}

/** Cards scheduled to become due during tomorrow's date window.
 *  Unseen cards (no attempts) are excluded — they aren't on any
 *  schedule. Overdue cards (dueAt < today) are also excluded so the
 *  "demain" count is a pure forward look, not a past-miss roll-up. */
export function countCardsDueTomorrow(
  cards: Card[],
  stats: StatsMap,
  now: Date = new Date(),
): number {
  const tomorrowStart = new Date(now);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);
  tomorrowStart.setHours(0, 0, 0, 0);
  const tomorrowEnd = new Date(tomorrowStart);
  tomorrowEnd.setHours(23, 59, 59, 999);
  const startMs = tomorrowStart.getTime();
  const endMs = tomorrowEnd.getTime();
  let n = 0;
  for (const c of cards) {
    const s = stats[c.id];
    if (!s || !s.attempts) continue;
    const due = s.dueAt || 0;
    if (due >= startMs && due <= endMs) n++;
  }
  return n;
}

export type RecapDelta = {
  /** Windowed label: "session" | "7d" | "30d" — caller maps to i18n. */
  window: "session" | "7d" | "30d";
  /** Positive delta in mastered cards over the window. 0 means none. */
  delta: number;
};

export type LevelChange = {
  from: CefrLevel;
  to: CefrLevel;
};

/** Mono-deck equivalent of LevelChange — a crossing of one of the
 *  MASTERY_TIER_THRESHOLDS during the session. `from` / `to` are
 *  1..5 (see MASTERY_MAX_TIER in domain/level). */
export type TierChange = {
  from: number;
  to: number;
};

export type SessionRecap = {
  /** Always-on: how many cards the user actually worked on this session
   *  and how many they got right. */
  session: { attempts: number; correct: number };
  /** Mastery delta vs the snapshot taken when the session started. */
  sessionDelta: number;
  /** Trailing-window deltas — `null` when the history doesn't go back
   *  far enough for the window to be meaningful. */
  weekDelta: number | null;
  monthDelta: number | null;
  /** CEFR level change during the session — only meaningful for
   *  bilingual decks. `null` when unchanged. */
  levelChange: LevelChange | null;
  /** Mastery-tier change during the session — only meaningful for
   *  mono decks. `null` when the tier (derived from mastered%) did
   *  not cross a threshold. Always computed so the UI can pick which
   *  one to render based on deck shape. */
  tierChange: TierChange | null;
};

export type RecapInputs = {
  cards: Card[];
  stats: StatsMap;
  session: { attempts: number; correct: number };
  /** Snapshot taken at session start (in-memory, not persisted). */
  sessionStartSnapshot: {
    masteredCount: number;
    userLevel: CefrLevel;
  };
  /** Current (end-of-session) CEFR estimate. */
  currentLevel: CefrLevel;
  /** Persisted daily-snapshot history for the active deck. */
  history: ProgressSnapshot[];
  /** Reference "now" for trailing-window lookups. Parameterized for
   *  testability. */
  now?: Date;
};

export function computeSessionRecap(inputs: RecapInputs): SessionRecap {
  const now = inputs.now ?? new Date();
  const masteredNow = computeMasteredCount(inputs.cards, inputs.stats);
  const sessionDelta = Math.max(
    0,
    masteredNow - inputs.sessionStartSnapshot.masteredCount,
  );
  const oldest = oldestSnapshotDate(inputs.history);

  // A window is "displayable" when the history stretches back AT
  // LEAST that far. We compare the window's start date to the oldest
  // snapshot we have — stricter than just counting entries, because
  // users can have sparse days (opened the app twice in 2 weeks).
  const windowDelta = (days: number): number | null => {
    if (!oldest) return null;
    const windowStart = dateNDaysAgo(days, now);
    if (oldest > windowStart) return null;
    const past = findSnapshotOnOrBefore(inputs.history, windowStart);
    if (!past) return null;
    return Math.max(0, masteredNow - past.masteredCount);
  };

  const levelChange: LevelChange | null =
    inputs.sessionStartSnapshot.userLevel !== inputs.currentLevel
      ? {
          from: inputs.sessionStartSnapshot.userLevel,
          to: inputs.currentLevel,
        }
      : null;

  // Mono-tier change: compare the tier derived from masteredCount at
  // snapshot time vs now. Uses the same 5-tier ladder as the pill
  // display. Always computed (cheap); UI decides whether to render
  // based on deck shape.
  const startTier = computeMasteryTier(
    inputs.sessionStartSnapshot.masteredCount,
    inputs.cards.length,
  );
  const endTier = computeMasteryTier(masteredNow, inputs.cards.length);
  const tierChange: TierChange | null =
    startTier !== endTier ? { from: startTier, to: endTier } : null;

  return {
    session: inputs.session,
    sessionDelta,
    weekDelta: windowDelta(7),
    monthDelta: windowDelta(30),
    levelChange,
    tierChange,
  };
}
